import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../auth/authActions";
import { useNavigate, useLocation } from "react-router-dom";
import { clone, map, mergeRight } from "ramda";
import { fetchSecurityUser } from "../util/fetchUtils";
import { notBlank } from "../util/ext";
import { useAuthenticationsHandler } from "./use-AuthenticationsHandler";
import { useSnackAlert } from "../common/use-snackAlert";
import { useSearchFilter } from "../common/use-searchFilter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";

import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import AuthenticationDialogs from "./AuthenticationDialogs";
import AuthenticationTable from "./AuthenticationTable";
import AuthenticationListButton from "./AuthenticationListButton";
import AuthenticationTypeSelect from "./AuthenticationTypeSelect";
import OrganizationAutoComplete from "../user-admin/OrganizationAutoComplete";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";
import RealTimeSearchBar from "../creator-application/common/RealTimeSearchBar";
import TotalsHeader from "../creator-application/common/TotalsHeader";

const authDialogInitialState = {
  referer: false,
  ezproxy: false,
  shibboleth: false,
  ip: false,
  barcode: false,
  sip: false,
  papi: false,
  region: false,
  scope: false,
};

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    root: {
      border: "3px white solid",
      backgroundColor: "white",
      borderRadius: "20px",
    },

    panel: {
      backgroundColor: "white",
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    listContainer: {
      height: "calc(100vh - 350px)",
      overflow: "auto",
    },
  }));

const userCache = new Map();
const lookupUser = (auth, userId) => {
  if (!userCache.has(userId)) {
    userCache.set(userId, fetchSecurityUser(auth)(userId));
  }

  return userCache.get(userId);
};

const AuthenticationBrowse = ({
  searchState,
  onSearchStateChange,
  searchResults,
}) => {
  const [scrollVisible, setScrollVisible] = useState(true);
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const classes = useStyles(scrollVisible)();
  const { SnackAlert, _showAlert } = useSnackAlert();
  const navigate = useNavigate();
  const location = useLocation();

  const [authentications, setAuthentications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // //-- Handle Search state
  const initialState = clone(searchState);
  const {
    state,
    handleUpdateSort,
    handleUpdateFilter,
    handleUpdatePage,
    handleUpdateOrganization,
    handleUpdateTypes,
  } = useSearchFilter(initialState);

  //-- Whenever the internal search state changes, update the search state stored in the parent
  useEffect(() => {
    onSearchStateChange(state);
    setScrollVisible(true);
  }, [onSearchStateChange, state]);

  useEffect(() => {
    if (searchResults.isError && searchResults.error.status === 401) {
      dispatch(logOut());
    }

    const fetchUsers = async authentication => {
      const createdByUser = await lookupUser(auth, authentication.createdBy);
      const updatedByUser = await lookupUser(auth, authentication.updatedBy);

      return mergeRight(authentication, {
        createdByUsername: createdByUser.username,
        updatedByUsername: updatedByUser.username,
      });
    };

    if (searchResults.isSuccess && notBlank(searchResults.data)) {
      setScrollVisible(true);
      setTotalCount(searchResults.data.count);
      console.log("Calling lookup user...");
      Promise.all(map(fetchUsers, searchResults.data.contents))
        .then(data => {
          console.log("Got the users...");
          setAuthentications(data);
        })
        .catch(err => {
          console.log(err);
        });
    }
  }, [
    dispatch,
    auth,
    searchResults.data,
    searchResults.isError,
    searchResults.error,
    searchResults.isSuccess,
  ]);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  const updateResults = useCallback(
    async authentication => {
      await searchResults.refetch();
    },
    [searchResults],
  );

  const {
    authDialogs,
    currentAuthentication,
    handleAuthenticationDeleteClick,
    handleAuthenticationMenuSelect,
    handleAuthenticationEditClick,
    handleAuthenticationDialogClose,
    handleAuthenticationDialogSave,
  } = useAuthenticationsHandler({
    initialDialogState: authDialogInitialState,
    organizationId: state.appliedOrganization,
    callbacks: {
      afterDelete: updateResults,
      afterSave: updateResults,
    },
  });

  return (
    <Container id="authentication-browse-view" classes={{ root: classes.root }}>
      <SnackAlert />
      <AuthenticationDialogs
        organizationId={state.appliedOrganization}
        dialogsState={authDialogs}
        currentAuthentication={currentAuthentication}
        onClose={handleAuthenticationDialogClose}
        onSave={handleAuthenticationDialogSave}
      />

      <div>
        <RealTimeSearchBar
          query={state.appliedFilter}
          onQueryChange={handleUpdateFilter}
          placeHolder="Search Details"
          backButton={
            notBlank(location.state) &&
            notBlank(location.state.previousLocation) && (
              <Button
                aria-label="Go back"
                onClick={() => navigate(location.state.previousLocation)}
                startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              >
                Back
              </Button>
            )
          }
          otherFilters={
            <Grid
              container
              alignItems="flex-end"
              direction="row"
              spacing={3}
              classes={{ root: classes.filters }}
            >
              <Grid item xs={12} md={8}>
                <OrganizationAutoComplete
                  value={state.appliedOrganization}
                  label="Filter By Organization"
                  onChange={handleUpdateOrganization}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <AuthenticationTypeSelect
                  value={state.appliedTypes}
                  label="Filter By Type"
                  onChange={handleUpdateTypes}
                />
              </Grid>
            </Grid>
          }
        />

        <TotalsHeader
          countText="Authentication"
          count={searchResults.isSuccess ? totalCount : "-"}
          selectedActions={
            <AuthenticationListButton
              title="New Authentication"
              onAuthenticationSelected={handleAuthenticationMenuSelect}
              variant="button"
            />
          }
        />

        {searchResults.isLoading && <PanelLoadingIndicator />}
        {searchResults.isSuccess && notBlank(authentications) && (
          <Container classes={{ root: classes.panel }}>
            <AuthenticationTable
              authenticationList={authentications}
              total={totalCount}
              page={state.page}
              orderBy={state.orderBy}
              order={state.orderDirection}
              onSortChange={handleUpdateSort}
              onPageChange={handleUpdatePage}
              onDeleteClicked={handleAuthenticationDeleteClick}
              onEditClicked={handleAuthenticationEditClick}
              // onSelectChange={handleSelectChange}
              onScrollVisible={handleScrollVisible}
            />
          </Container>
        )}
      </div>
    </Container>
  );
};

export default AuthenticationBrowse;
